<template>
  <!-- // TODO: [Sportybet] -->
  <div>
    <div class="p-0 transactions">
      <div
        v-for="transaction in transactionsToShow"
        :key="transaction.tradeId"
        class="m-b-10 transaction"
        :class="{
          expanded: expandedTrans.includes(transaction.tradeId),
          'non-selectable-item': submitted && !isTransactionSelected(transaction),
        }"
      >
        <div class="transaction__content">
          <div class="p-1 transaction__header">
            <template v-if="!viewOnly">
              <span v-if="isTransactionSelected(transaction)" @click="toggleTransactionSelection(transaction)">
                <FaCheckSquare height="2.1rem" width="2.1rem" class="checked transaction__checkbox" />
              </span>
              <span v-else @click="toggleTransactionSelection(transaction)">
                <FaRegSquare height="2.1rem" width="2.1rem" class="transaction__checkbox unchecked" />
              </span>
            </template>
            <div class="separate" />
            <div class="title">
              <div class="title__description">
                {{ actionType }} {{ preposition }} {{ transaction.counterAuthority }}
                {{ transaction.amount | formatMoney }} {{ currency }}.
              </div>
              <div class="font-size-sm title__tradeId">
                {{ $t('page.issue.transaction.id') }} {{ transaction.tradeId }}
              </div>
              <div class="title__details">
                <span>Date - {{ transaction.createTime | formatDay }}</span>
                <span>Time - {{ transaction.createTime | formatTime }}</span>
                <span class="title__details__status"
                  >{{ $t('page.issue.status') }} - {{ TRANSACTION_STATUS_NAME_ENUM[transaction.status] }}</span
                >
              </div>
            </div>
            <div class="arrow" @click.prevent="expandTrans(transaction.tradeId)" />
          </div>
          <div v-if="expandedTrans.includes(transaction.tradeId)" class="transaction__details">
            <div class="transaction__details--amount">
              <span>{{ $t('page.issue.amount') }} ({{ currency }})</span>
              <span class="amount" :class="{ withdrawal: transactionType === 2 }">
                {{ operator }}
                {{ transaction.amount | formatMoney }}
              </span>
            </div>
            <div class="transaction__details--status">
              <span>{{ $t('page.issue.status') }}</span>
              <span class="data">{{ TRANSACTION_STATUS_NAME_ENUM[transaction.status] }}</span>
            </div>
            <div class="transaction__details--time">
              <span>Time</span>
              <span class="data">{{ transaction.createTime | formatDate }}</span>
            </div>
            <div class="transaction__details--from">
              <span>{{ actionType }} {{ preposition }}</span>
              <span class="data">{{ transaction.counterFull }}</span>
            </div>
            <div class="transaction__details--tradeno">
              <span>{{ $t('page.issue.transaction.trade_no') }}</span>
              <span class="data">{{ transaction.tradeId }}</span>
            </div>
            <div v-if="transaction.payChTxId" class="transaction__details--transno">
              <span>{{ $t('page.issue.transaction.no') }}</span>
              <span class="data">{{ transaction.payChTxId }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <ThemeButton
      v-if="!submitted && !viewOnly"
      block
      :disabled="!selectedTransaction || !selectedTransaction.length"
      class="p-1"
      @click="proceed"
    >
      Submit
    </ThemeButton>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { TransStatement } from '@/common/api/model';
import { TransactionType } from '@/common/config';
import { formatMoney } from '@/common/utils';
import {
  TRANSACTION_PREPOSITION_ENUM,
  TRANSACTION_STATUS_ENUM,
  TRANSACTION_STATUS_NAME_ENUM,
} from '@/common/transaction-utils';

import MdRadioButtonChecked from '@/components/icons/MdRadioButtonChecked.vue';
import MdRadioButtonUnchecked from '@/components/icons/MdRadioButtonUnchecked.vue';

import FaCheckSquare from '@/components/icons/FaCheckSquare.vue';
import FaRegSquare from '@/components/icons/FaRegSquare.vue';

import ThemeButton from '@/components/ThemeButton.vue';

const userModule = namespace('user');

@Component({
  components: {
    ThemeButton,
    MdRadioButtonChecked,
    MdRadioButtonUnchecked,
    FaCheckSquare,
    FaRegSquare,
  },
  filters: {
    formatDate(value: any) {
      const options = {
        hour12: false,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      } as const;
      const date = new Date(value);
      return date.toLocaleString(undefined, options);
    },
    formatDay(value: any) {
      const options = { hour12: false, year: 'numeric', month: 'numeric', day: 'numeric' } as const;
      const date = new Date(value);
      return date.toLocaleString(undefined, options);
    },
    formatTime(value: any) {
      const options = { hour12: false, hour: 'numeric', minute: 'numeric', second: 'numeric' } as const;
      const date = new Date(value);
      return date.toLocaleString(undefined, options);
    },
    formatMoney(value: number) {
      return formatMoney(value);
    },
  },
})
export default class TransactionItems extends Vue {
  @Prop({ default: () => [] }) readonly transactions!: TransStatement[];
  @Prop({ default: '' }) readonly transactionType!: TransactionType;
  @Prop(Boolean) readonly viewOnly!: boolean;

  @userModule.Getter('currency') currency!: string;
  selectedTransaction: TransStatement[] = [];

  readonly TRANSACTION_PREPOSITION_ENUM = TRANSACTION_PREPOSITION_ENUM;
  readonly TRANSACTION_STATUS_ENUM = TRANSACTION_STATUS_ENUM;
  readonly TRANSACTION_STATUS_NAME_ENUM = TRANSACTION_STATUS_NAME_ENUM;

  expandedTrans: string[] = [];
  submitted = false;

  get actionType() {
    return TransactionType[this.transactionType];
  }
  get preposition() {
    return this.TRANSACTION_PREPOSITION_ENUM[this.transactionType];
  }
  get isNoRecord() {
    return this.transactions.length === 0;
  }
  get operator() {
    return this.transactionType === TransactionType.Deposit ? '+' : '-';
  }
  get isDepositList() {
    return this.transactionType === TransactionType.Deposit;
  }

  get transactionsToShow() {
    return this.transactions;
  }

  toggleTransactionSelection(transaction: TransStatement) {
    if (this.submitted) return;
    if (this.selectedTransaction.includes(transaction)) {
      this.selectedTransaction = this.selectedTransaction.filter((t) => t !== transaction);
    } else {
      this.selectedTransaction = [...this.selectedTransaction, transaction];
    }
  }

  expandTrans(tradeId: string) {
    const index = this.expandedTrans.indexOf(tradeId);
    const exist = index !== -1;
    if (exist) {
      this.expandedTrans.splice(index, 1);
    } else {
      this.expandedTrans.push(tradeId);
    }
  }

  isTransactionSelected(transaction: TransStatement) {
    return this.selectedTransaction.includes(transaction);
  }

  proceed() {
    if (this.viewOnly) return;
    const selectedList = this.selectedTransaction;
    if (!selectedList?.length || this.submitted) return;

    const issueMessages = selectedList.reduce((acc: string[], cur) => {
      const authority = cur.counterAuthority ? cur.counterAuthority : cur.counterFull;
      const msg = `${this.actionType} ${authority ? this.preposition : ''} ${authority || ''} ${formatMoney(
        cur.amount
      )} ${this.currency} (transaction id: ${cur.tradeId})`;
      return [...acc, msg];
    }, []);

    this.$emit('input', { type: this.transactionType, issueMessages, selected: selectedList });
    this.submitted = true;
  }
}
</script>

<style lang="scss">
@import '@/views/Chatroom/RuleEngineHelper/Transaction/index.scss';
.p-0.p-0.p-0 {
  padding: 0;
}

.p-1.p-1.p-1 {
  padding: 1rem;
}
.font-size-sm.font-size-sm {
  font-size: $size-xs;
}
.transaction__details .data.data {
  text-align: right;
}
</style>
